import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

export interface HeroSectionProps {
  salonName: string;
  slogan: string;
  description: string;
  logo?: string;
  salonId?: string;
}

const HeroSection: React.FC<HeroSectionProps> = ({ salonName, slogan, description, logo, salonId }) => {
  const { slug } = useParams<{ slug: string }>();
  const [logoError, setLogoError] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  
  // Logo URL'sini al ve formatla
  const getLogoUrl = (logoPath: string | undefined): string => {
    if (!logoPath) return '';
    
    // Eğer logo tam URL ise (http veya https ile başlıyorsa) doğrudan kullan
    if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
      return logoPath;
    }
    
    // API'nin yüklediği logoları için base URL ekle
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.345.tr';
    console.log('Hero logo path:', logoPath);
    
    // URL'de /v1 yolunu kontrol et ve yoksa ekle
    if (logoPath.startsWith('/uploads') && !baseUrl.endsWith('/v1')) {
      return `${baseUrl}/v1${logoPath}`;
    }
    
    return `${baseUrl}${logoPath}`;
  };
  
  // Arka plan görüntüsünü ayarlama
  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (!salonId) {
        console.error('HeroSection: salonId prop\'u bulunamadı, arka plan ayarlanamadı!');
        return;
      }
      
      try {
        console.log('Arka plan fotoğrafı alınıyor... SalonID:', salonId);
        // API'den rastgele fotoğraf al
        const apiUrl = `https://api.345.tr/v1.3/photos/random/${salonId}?type=salon&t=${Date.now()}`;
        const response = await axios.get(apiUrl);
        
        if (response.data && response.data.success && response.data.photo && response.data.photo.photoUrl) {
          console.log('Arka plan fotoğrafı başarıyla alındı:', response.data.photo.photoUrl);
          
          let photoUrl = response.data.photo.photoUrl;
          const fullUrl = response.data.photo.photoUrl.startsWith('http') 
            ? response.data.photo.photoUrl 
            : `https://api.345.tr/v1.3${photoUrl}?t=${Date.now()}`;
            
          setBackgroundImage(fullUrl);
        } else {
          console.error('API\'den arka plan fotoğrafı alınamadı:', response.data);
        }
      } catch (error) {
        console.error('Arka plan fotoğrafı yüklenemedi:', error);
      }
    };
    
    fetchBackgroundImage();
  }, [salonId]);
  
  // Salon ID'yi global olarak tut (arka plan için)
  useEffect(() => {
    if (salonId) {
      console.log('HeroSection: Salon ID global window değişkenine atanıyor:', salonId);
      (window as any).SALON_ID = salonId;
      
      // Debug için konsolda göster
      setTimeout(() => {
        console.log('HeroSection: window.SALON_ID kontrol:', (window as any).SALON_ID);
      }, 200);
    } else {
      console.error('HeroSection: salonId prop\'u bulunamadı!');
    }
  }, [salonId]);
  
  const handleLogoError = () => {
    console.error('Hero logo yüklenemedi:', logo);
    setLogoError(true);
  };

  return (
    <div>
      <HeroContainer>
        {backgroundImage && (
          <BackgroundImage src={backgroundImage} alt="Salon Arka Plan" />
        )}
        <HeroOverlay />
        <HeroContent>
          {logo && !logoError && (
            <LogoContainer>
              <LogoImage 
                src={getLogoUrl(logo)} 
                alt={salonName} 
                onError={handleLogoError}
              />
            </LogoContainer>
          )}
          <HeroTitle>{salonName}</HeroTitle>
          <HeroSubtitle>{slogan}</HeroSubtitle>
          <HeroDescription>
            {description}
          </HeroDescription>
          <HeroButtons>
            <PrimaryButton to={`/${slug}/randevu`}>Randevu Al</PrimaryButton>
            <SecondaryButton to={`/${slug}/hizmetler`}>Hizmetlerimiz</SecondaryButton>
          </HeroButtons>
        </HeroContent>
        <ScrollDown href="#services">
          <span className="material-icons">expand_more</span>
        </ScrollDown>
      </HeroContainer>
    </div>
  );
};

const HeroContainer = styled.section`
  position: relative;
  height: 100vh;
  min-height: 600px;
  max-height: 900px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  color: white;
  overflow: hidden;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    145deg,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.65) 50%,
    rgba(0, 0, 0, 0.4) 100%
  );
  z-index: 1;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 40%;
    background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
    z-index: 2;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 900px;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 60px 70px;
  border-radius: 16px;
  backdrop-filter: blur(8px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -10%;
    width: 120%;
    height: 5px;
    background: linear-gradient(to right, transparent, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary}, transparent);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 40px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 30px;
  }
`;

const LogoContainer = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  margin: 0 auto 20px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    padding: 5px;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background-color: white;
`;

const HeroTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 800;
  margin-bottom: 25px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  letter-spacing: 2px;
  animation: fadeInUp 1s ease-out;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.8rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 2.2rem;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(40px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeroSubtitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 300;
  margin-bottom: 25px;
  color: white;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: 1.2px;
  animation: fadeInUp 1s ease-out 0.2s both;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.6rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.4rem;
  }
`;

const HeroDescription = styled.p`
  font-size: 1.15rem;
  margin-bottom: 40px;
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Poppins', sans-serif;
  font-weight: 300;
  animation: fadeInUp 1s ease-out 0.4s both;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 1.05rem;
    line-height: 1.7;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 25px;
  justify-content: center;
  animation: fadeInUp 1s ease-out 0.6s both;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: white;
  padding: 16px 36px;
  position: relative;
  border-radius: 50px;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 1px;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50px;
    padding: 2px;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    z-index: -2;
    border-radius: 50px;
  }
  
  &:hover {
    color: white;
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 157, 255, 0.3);
    
    &:after {
      left: 0;
    }
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: white;
  padding: 16px 36px;
  position: relative;
  border-radius: 50px;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 1px;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  overflow: hidden;
  z-index: 1;
  
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid white;
    border-radius: 50px;
    z-index: -2;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background: white;
    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
    z-index: -1;
    border-radius: 50px;
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(255, 255, 255, 0.2);
    
    &:before {
      width: 100%;
    }
    
    &:after {
      border-color: white;
    }
  }
`;

const ScrollDown = styled.a`
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  text-decoration: none;
  z-index: 10;
  animation: bounce 2s infinite;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: translateX(-50%) scale(1.1);
  }
  
  span {
    font-size: 32px;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-15px);
    }
    60% {
      transform: translateX(-50%) translateY(-7px);
    }
  }
`;

const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
`;

export default HeroSection; 